/**
 * Phone Number Utilities
 * Handles E.164 formatting and validation
 *
 * E.164 is an ITU-T international standard defining a uniform, max 15-digit format
 * for phone numbers to ensure global uniqueness and proper routing on the PSTN.
 * Format: + [country code] [subscriber number] (e.g., +14155552671)
 */

/**
 * Convert E.164 format to display format
 * @param {string} e164 - Phone number in E.164 format (e.g., "+14155552671")
 * @returns {string} Formatted phone number (e.g., "+1 (415) 555-2671")
 */
export const formatPhoneDisplay = (e164) => {
  if (!e164) return '';

  // Remove all non-digit characters except +
  const cleaned = e164.replace(/[^\d+]/g, '');

  // Check if it's a US/Canada number (starts with +1)
  if (cleaned.startsWith('+1') && cleaned.length === 12) {
    const areaCode = cleaned.substring(2, 5);
    const prefix = cleaned.substring(5, 8);
    const lineNumber = cleaned.substring(8, 12);
    return `+1 (${areaCode}) ${prefix}-${lineNumber}`;
  }

  // For other international numbers, just return as-is
  return cleaned;
};

/**
 * Convert user input to E.164 format
 * @param {string} input - User input (various formats)
 * @returns {string} Phone number in E.164 format (e.g., "+14155552671")
 */
export const toE164 = (input) => {
  if (!input) return '';

  // Remove all non-digit characters
  let cleaned = input.replace(/\D/g, '');

  // If it starts with 1 and is 11 digits, it's a US/Canada number
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }

  // If it's 10 digits, assume US/Canada and prepend +1
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }

  // If input had a +, preserve it
  if (input.trim().startsWith('+')) {
    return `+${cleaned}`;
  }

  // For other cases, return cleaned digits (may need manual + prefix)
  return cleaned;
};

/**
 * Validate E.164 format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid E.164 format
 */
export const isValidE164 = (phone) => {
  if (!phone) return false;

  // E.164 format: + followed by 1-15 digits
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  return e164Regex.test(phone);
};

/**
 * Format phone number as user types (for input field)
 * @param {string} value - Current input value
 * @returns {string} Formatted value for display
 */
export const formatPhoneInput = (value) => {
  if (!value) return '';

  // Remove all non-digit characters
  let cleaned = value.replace(/\D/g, '');
  // If it starts with '1' and has 11 digits (US/Canada with country code), remove the leading '1'
  if (cleaned.length === 11 && cleaned[0] === '1') {
    cleaned = cleaned.substring(1);
  }
  // Format as user types for US/Canada numbers (expecting 10 digits)
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return `+1 (${cleaned}`;
  if (cleaned.length <= 6) return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  if (cleaned.length <= 10) {
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  // Limit to 10 digits for US/Canada (ignore extra digits)
  return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

/**
 * Parse phone number object from various input formats
 * @param {Object|string} input - Phone number data
 * @returns {Object} Normalized phone number object {name, description, phone, extension}
 */
export const normalizePhoneNumber = (input) => {
  if (typeof input === 'string') {
    return {
      name: '',
      description: '',
      phone: toE164(input),
      extension: ''
    };
  }

  if (typeof input === 'object' && input !== null) {
    return {
      name: input.name || input.type || '',
      description: input.description || '',
      phone: input.phone ? toE164(input.phone) : '',
      extension: input.extension || ''
    };
  }

  return {
    name: '',
    description: '',
    phone: '',
    extension: ''
  };
};

/**
 * Get display string for a phone number object
 * @param {Object} phoneObj - Phone number object {name, description, phone, extension}
 * @returns {string} Display string (e.g., "Mobile: +1 (415) 555-2671 ext. 123")
 */
export const getPhoneDisplayString = (phoneObj) => {
  if (!phoneObj || !phoneObj.phone) return '';

  const parts = [];

  if (phoneObj.name) parts.push(phoneObj.name);
  parts.push(formatPhoneDisplay(phoneObj.phone));
  if (phoneObj.extension) parts.push(`ext. ${phoneObj.extension}`);

  return parts.join(' - ');
};
